import { useState, useEffect, lazy, Suspense } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutGrid, Plus } from 'lucide-react';

import { trackEvent } from '../utils/analytics';
import { useApp } from '../context/AppContext';
import { NAVIGATION_CONFIGS } from './navigation/config/navigationConfigs';

const WorkspaceSwitcherModal = lazy(() => import('./navigation/modals/WorkspaceSwitcherModal'));
const BetaSandboxModal = lazy(() => import('./navigation/modals/BetaSandboxModal'));
const CreateHangoutModal = lazy(() => import('./navigation/modals/CreateHangoutModal'));
const WrenchVehicleSelectorModal = lazy(() => import('../features/garage/components/WrenchVehicleSelectorModal'));
const WrenchLogOptionsModal = lazy(() => import('../features/garage/components/WrenchLogOptionsModal'));
const BarkActionDrawerModal = lazy(() => import('../features/bark/components/BarkActionDrawerModal'));
const CreateSocialPostModal = lazy(() => import('../features/garage/components/CreateSocialPostModal'));

export default function Navigation({ user, settings }) {
    const location = useLocation();
    const navigate = useNavigate();
    const { appContext, setAppContext, vehicles } = useApp();

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showBetaModal, setShowBetaModal] = useState(false);
    const [showAppSwitcher, setShowAppSwitcher] = useState(false);
    const [showVehicleSelector, setShowVehicleSelector] = useState(false);
    const [showBarkActionDrawer, setShowBarkActionDrawer] = useState(false);
    const [showLogOptionsModal, setShowLogOptionsModal] = useState(false);
    const [showSocialPostModal, setShowSocialPostModal] = useState(false);

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        if (searchParams.get('openLog') === 'true' || searchParams.get('openQuickLogTiles') === 'true') {
            setShowLogOptionsModal(true);

            // Clean up the URL query parameter so refresh doesn't keep opening it
            const newUrl = window.location.pathname;
            window.history.replaceState({}, document.title, newUrl);
        }
    }, [location.pathname, location.search]);

    // Only show if user is logged in
    if (!user) return null;

    const currentConfig = NAVIGATION_CONFIGS[appContext] || NAVIGATION_CONFIGS.HANGOUT;

    // Smart settings gear redirection parameters
    const pathParts = location.pathname.split('/');
    const activePetId = pathParts[1] === 'bark' && pathParts[2] && pathParts[2] !== 'feed' && pathParts[2] !== 'settings' ? pathParts[2] : null;
    const activeVehicleId = pathParts[1] === 'garage' && pathParts[2] && pathParts[2] !== 'logs' && pathParts[2] !== 'reporting' && pathParts[2] !== 'settings' ? pathParts[2] : null;
    const isVehiclePage = !!activeVehicleId;

    // Central "+" Action Click
    const handlePlusClick = () => {
        if (appContext === 'WRENCH') {
            const isSocialTab = location.pathname === '/garage/feed' && (new URLSearchParams(location.search).get('tab') !== 'fleet');
            if (isSocialTab) {
                setShowSocialPostModal(true);
                trackEvent('GARAGE_SOCIAL_POST_MODAL_OPEN');
            } else if (isVehiclePage) {
                setShowLogOptionsModal(true);
                trackEvent('GARAGE_LOG_TILES_MENU_OPEN', { vehicleId: activeVehicleId });
            } else {
                setShowVehicleSelector(true);
                trackEvent('GARAGE_VEHICLE_SELECTOR_OPEN');
            }
        } else if (appContext === 'BARK') {
            setShowBarkActionDrawer(true);
            trackEvent('BARK_ACTION_DRAWER_OPEN', { petId: activePetId });
        } else {
            setShowCreateModal(true);
            trackEvent('CREATE_MODAL_OPEN', { source: 'nav_center' });
        }
    };

    const isProfileActive = location.pathname === `/u/${user.id}`;

    return (
        <>
            <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-30 animate-fade-in-up">
                <div className="flex items-center gap-2 bg-neutral-900/90 backdrop-blur-md px-4 py-2 rounded-full border border-neutral-700 shadow-2xl ring-1 ring-white/10">

                    {/* Google-Style 9-Dot Workspace Switcher */}
                    <button
                        onClick={() => {
                            setShowAppSwitcher(true);
                            trackEvent('APP_SWITCHER_OPEN');
                        }}
                        className={`p-2 rounded-full transition relative group ${showAppSwitcher ? currentConfig.switcherActiveColor : 'text-neutral-500 hover:text-white'}`}
                        title="Switch Workspace"
                        aria-label="Switch Workspace"
                    >
                        <LayoutGrid className="w-5 h-5" />
                        {showAppSwitcher && (
                            <span className={`absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full ${currentConfig.switcherIndicatorClass}`} />
                        )}
                    </button>

                    {/* Dynamic Left Navigation Links */}
                    {currentConfig.leftNavItems.map((item) => {
                        const Icon = item.icon;
                        const active = item.isMatch(location.pathname);
                        return (
                            <Link
                                key={item.to}
                                to={item.to}
                                className={`p-2 rounded-full transition relative group ${active ? item.activeColor : 'text-neutral-500 hover:text-white'}`}
                                aria-label={item.label}
                            >
                                <Icon className="w-5 h-5" />
                                {active && (
                                    <span className={`absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full ${item.indicatorClass}`} />
                                )}
                            </Link>
                        );
                    })}

                    {/* Central Action Button */}
                    {currentConfig.hasPlusButton && (
                        <button
                            onClick={handlePlusClick}
                            className={`bg-gradient-to-tr ${currentConfig.plusButtonGradient} p-2.5 rounded-full shadow-lg mx-2 hover:scale-110 transition border-4 border-neutral-900 -mt-8 relative z-10 group`}
                            aria-label={currentConfig.plusButtonAriaLabel}
                        >
                            <Plus className="w-6 h-6 text-white group-hover:rotate-90 transition duration-300" strokeWidth={3} />
                        </button>
                    )}

                    {/* Dynamic Right Navigation Links */}
                    {currentConfig.rightNavItems.map((item) => {
                        const Icon = item.icon;
                        const active = item.isMatch(location.pathname);
                        return (
                            <Link
                                key={item.to}
                                to={item.to}
                                className={`p-2 rounded-full transition relative group ${active ? item.activeColor : 'text-neutral-500 hover:text-white'}`}
                                aria-label={item.label}
                            >
                                <Icon className="w-5 h-5" />
                                {active && (
                                    <span className={`absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full ${item.indicatorClass}`} />
                                )}
                            </Link>
                        );
                    })}

                    {/* User Profile Avatar Link */}
                    <Link
                        to={`/u/${user.id}`}
                        className={`p-2 rounded-full transition relative group ${isProfileActive ? 'text-white' : 'text-neutral-500 hover:text-white'}`}
                        aria-label="My Profile"
                    >
                        <div className={`w-6 h-6 rounded-full overflow-hidden border-2 transition ${isProfileActive ? 'border-white shadow-[0_0_8px_rgba(255,255,255,0.5)]' : 'border-transparent'}`}>
                            {user.avatar ? (
                                <img src={user.avatar} className="w-full h-full object-cover" alt={user.name || 'Profile'} />
                            ) : (
                                <div className="w-full h-full bg-neutral-600" aria-label="Default profile avatar"></div>
                            )}
                        </div>
                    </Link>
                </div>

                {/* Dynamic Gradient glow underneath */}
                <div className={`absolute -bottom-4 left-1/2 -translate-x-1/2 w-3/4 h-12 ${currentConfig.glowClass} blur-2xl rounded-full -z-10 pointer-events-none transition-colors duration-500`} />
            </div>

            {/* Modals & Action Drawers */}
            {showAppSwitcher && (
                <Suspense fallback={null}>
                    <WorkspaceSwitcherModal
                        isOpen={showAppSwitcher}
                        onClose={() => setShowAppSwitcher(false)}
                        user={user}
                        settings={settings}
                        appContext={appContext}
                        setAppContext={setAppContext}
                        navigate={navigate}
                        trackEvent={trackEvent}
                        onOpenBetaModal={() => setShowBetaModal(true)}
                    />
                </Suspense>
            )}

            {showBetaModal && (
                <Suspense fallback={null}>
                    <BetaSandboxModal
                        isOpen={showBetaModal}
                        onClose={() => setShowBetaModal(false)}
                    />
                </Suspense>
            )}

            {showVehicleSelector && (
                <Suspense fallback={null}>
                    <WrenchVehicleSelectorModal
                        isOpen={showVehicleSelector}
                        onClose={() => setShowVehicleSelector(false)}
                        vehicles={vehicles}
                        navigate={navigate}
                    />
                </Suspense>
            )}

            {showBarkActionDrawer && (
                <Suspense fallback={null}>
                    <BarkActionDrawerModal
                        isOpen={showBarkActionDrawer}
                        onClose={() => setShowBarkActionDrawer(false)}
                        activePetId={activePetId}
                        navigate={navigate}
                    />
                </Suspense>
            )}

            {showCreateModal && (
                <Suspense fallback={null}>
                    <CreateHangoutModal
                        isOpen={showCreateModal}
                        onClose={() => setShowCreateModal(false)}
                        user={user}
                        navigate={navigate}
                    />
                </Suspense>
            )}

            {showLogOptionsModal && (
                <Suspense fallback={null}>
                    <WrenchLogOptionsModal
                        isOpen={showLogOptionsModal}
                        onClose={() => setShowLogOptionsModal(false)}
                        activeVehicleId={activeVehicleId}
                        trackEvent={trackEvent}
                    />
                </Suspense>
            )}

            {showSocialPostModal && (
                <Suspense fallback={null}>
                    <CreateSocialPostModal
                        isOpen={showSocialPostModal}
                        onClose={() => setShowSocialPostModal(false)}
                    />
                </Suspense>
            )}
        </>
    );
}
